/** @jsxImportSource @emotion/react */
import styled from '@emotion/styled/macro'
import { t } from '@sportninja/common/i18n'
import { colors } from '@sportninja/common/constants/appColors'
import {
  captionXSmall,
  paragraphSmall,
  weightSemiBold,
} from 'src/components/css'
import { Link } from 'react-router-dom'
import { isCanlan } from '@sportninja/common/utils/customer-name'

const Container = styled.div`
  align-self: stretch;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 8px;
  display: inline-flex;
  ${(props) => (props.isMobile ? 'padding: 0 0;' : '')}
`

const Header = styled.div`
  align-self: stretch;
  justify-content: space-between;
  align-items: center;
  display: inline-flex;
`

const Title = styled.div`
  ${paragraphSmall}
  ${weightSemiBold}
  justify-content: center;
  display: flex;
  flex-direction: column;
  color: ${colors.NEUTRAL_50};
  text-transform: uppercase;
  line-height: 16.8px;
  letter-spacing: 1.68px;
  word-wrap: break-word;
`

const GoToStatsLink = styled(Link)`
  text-align: center;
  justify-content: center;
  display: flex;
  flex-direction: column;
  color: ${colors.PRIMARY};
  font-size: 12px;
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  line-height: 17.4px;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`

const StatsCard = styled.div`
  align-self: stretch;
  padding: 16px;
  background: ${colors.SECONDARY_300};
  border-radius: 8px;
  outline: 1px ${colors.SECONDARY_200} solid;
  outline-offset: -1px;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 8px;
  display: flex;
`

const StatRow = styled.div`
  align-self: stretch;
  justify-content: flex-start;
  align-items: center;
  gap: 16px;
  display: inline-flex;
`

const StatNumber = styled.div`
  flex: 1 1 0;
  justify-content: center;
  display: flex;
  flex-direction: column;
  color: ${colors.NEUTRAL_0};
  font-size: 24px;
  font-family: ${isCanlan
    ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif !important;'
    : 'BarlowCondensed, Arial, Helvetica, sans-serif !important;'};
  font-weight: 600;
  line-height: 28.8px;
  word-wrap: break-word;
  ${(props) => props.isRight && 'text-align: right;'}
`

const StatLabel = styled.div`
  ${captionXSmall}
  ${weightSemiBold}
  text-align: center;
  justify-content: center;
  display: flex;
  flex-direction: column;
  color: ${colors.NEUTRAL_50};
  text-transform: uppercase;
  word-wrap: break-word;
`

const BarContainer = styled.div`
  align-self: stretch;
  height: 8px;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 8px;
  display: flex;
`

const Bar = styled.div`
  height: 8px;
  background: ${(props) => props.color};
  border-radius: 24px;
  flex: ${(props) => props.flex};
  min-width: 8px;
`

const StatRowComponent = ({ left = 0, right = 0, title }) => {
  const total = left + right
  let leftFlex = left
  let rightFlex = right

  // Handle edge cases where both values are 0
  if (leftFlex === 0 && rightFlex === 0) {
    leftFlex = 1
    rightFlex = 1
  }
  // Ensure minimum flex value for visibility
  if (leftFlex === 0) leftFlex = 0.1
  if (rightFlex === 0) rightFlex = 0.1

  return (
    <>
      <StatRow>
        <StatNumber>{left}</StatNumber>
        <StatLabel>{title}</StatLabel>
        <StatNumber isRight>{right}</StatNumber>
      </StatRow>
      <BarContainer>
        <Bar color='#999999' flex={leftFlex} />
        <Bar color='#ed1d26' flex={rightFlex} />
      </BarContainer>
    </>
  )
}

const GameDetailsStats = ({
  game,
  isMobile,
  isSoccer = false,
  isGenericGame = false,
  statsUrl,
}) => {
  if (!game.started) {
    return false
  }

  return (
    <Container isMobile={isMobile}>
      <Header>
        <Title>{t('GameScreen:teamStats')}</Title>
        {statsUrl && <GoToStatsLink to={statsUrl}>Go to Stats</GoToStatsLink>}
      </Header>
      <StatsCard>
        <StatRowComponent
          left={game.homeTeamScore}
          right={game.visitingTeamScore}
          title={
            isGenericGame ? 'Points' : t('common:goal_plural', { count: 2 })
          }
        />
        <StatRowComponent
          left={game.homeOffenseCount}
          right={game.visitingOffenseCount}
          title={
            isSoccer || isGenericGame
              ? 'Fouls'
              : t('common:penalty_plural', { count: 2 })
          }
        />
        <StatRowComponent
          left={game.homeShotCount}
          right={game.visitingShotCount}
          title={t('common:shot_plural', { count: 2 })}
        />
      </StatsCard>
    </Container>
  )
}

export default GameDetailsStats
